import { useState, useEffect } from 'react';
import axios from 'axios';
import { Cloud, CloudRain, Sun, Wind, Droplets, AlertTriangle, Crown, Calendar } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import './WeatherWidget.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function WeatherWidget({ latitude = 56.5, longitude = -4.2 }) {
  const { token, isPremium } = useAuth();
  const [weatherData, setWeatherData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForecast, setShowForecast] = useState(false);

  useEffect(() => {
    fetchWeather();
    // Refresh every 30 minutes
    const interval = setInterval(fetchWeather, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, [latitude, longitude, token]);

  const fetchWeather = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(
        `${API}/weather/forecast?latitude=${latitude}&longitude=${longitude}`,
        { headers }
      );
      setWeatherData(response.data);
    } catch (error) {
      console.error('Error fetching weather:', error);
      // Fallback to basic weather
      try {
        const basicResponse = await axios.get(`${API}/weather?latitude=${latitude}&longitude=${longitude}`);
        setWeatherData({
          current: basicResponse.data,
          forecast: []
        });
      } catch (fallbackError) {
        console.error('Error fetching basic weather:', fallbackError);
      }
    } finally {
      setLoading(false);
    }
  };
  
  const weather = weatherData?.current;

  const getWeatherIcon = (condition) => {
    const icons = {
      sunny: <Sun size={32} />,
      cloudy: <Cloud size={32} />,
      rainy: <CloudRain size={32} />,
      windy: <Wind size={32} />
    };
    return icons[condition] || <Cloud size={32} />;
  };

  const getConditionColor = (condition) => {
    const colors = {
      sunny: '#fbbf24',
      cloudy: '#94a3b8',
      rainy: '#60a5fa',
      windy: '#a78bfa'
    };
    return colors[condition] || '#94a3b8';
  };

  if (loading) {
    return (
      <div className="weather-widget loading" data-testid="weather-loading">
        <div className="loading-spinner small"></div>
      </div>
    );
  }

  if (!weather) return null;

  const getConditionText = (condition) => {
    const texts = {
      sunny: 'Sonnig',
      cloudy: 'Bewölkt',
      rainy: 'Regnerisch',
      windy: 'Windig'
    };
    return texts[condition] || condition;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const days = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
    return days[date.getDay()];
  };

  return (
    <div className="weather-widget" data-testid="weather-widget">
      {weather.alert && (
        <div className="weather-alert" data-testid="weather-alert">
          <AlertTriangle size={20} />
          <span>{weather.alert}</span>
        </div>
      )}

      <div className="weather-main" style={{ borderLeftColor: getConditionColor(weather.condition) }}>
        <div className="weather-icon" style={{ color: getConditionColor(weather.condition) }}>
          {getWeatherIcon(weather.condition)}
        </div>
        
        <div className="weather-info">
          <div className="weather-temp">{weather.temperature_c}°C</div>
          <div className="weather-condition">{getConditionText(weather.condition)}</div>
        </div>

        <div className="weather-details">
          <div className="weather-detail">
            <Wind size={18} />
            <span>{weather.wind_speed_kmh} km/h {weather.wind_direction}</span>
          </div>
          <div className="weather-detail">
            <Droplets size={18} />
            <span>{weather.precipitation_mm} mm</span>
          </div>
        </div>
      </div>

      {/* 7-Day Forecast Section */}
      {isPremium && weatherData?.forecast && weatherData.forecast.length > 0 && (
        <div className="weather-forecast-section">
          <button 
            className="forecast-toggle"
            onClick={() => setShowForecast(!showForecast)}
          >
            <Calendar size={18} />
            <span>7-Tage Vorhersage</span>
            <span className="premium-badge-mini">
              <Crown size={14} />
              Premium
            </span>
          </button>

          {showForecast && (
            <div className="forecast-grid">
              {weatherData.forecast.map((day, index) => (
                <div key={index} className="forecast-day">
                  <div className="forecast-day-name">{formatDate(day.date)}</div>
                  <div className="forecast-icon" style={{ color: getConditionColor(day.condition) }}>
                    {getWeatherIcon(day.condition)}
                  </div>
                  <div className="forecast-temps">
                    <span className="temp-high">{day.temperature_high_c}°</span>
                    <span className="temp-low">{day.temperature_low_c}°</span>
                  </div>
                  <div className="forecast-precipitation">
                    <Droplets size={14} />
                    {day.precipitation_chance}%
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Premium Teaser for Free Users */}
      {!isPremium && (
        <div className="weather-premium-teaser">
          <Crown size={18} />
          <span>Upgrade auf Premium für 7-Tage Wettervorhersage</span>
        </div>
      )}
    </div>
  );
}
